import { Divider, Skeleton, Stack, Typography } from '@mui/material';
import type { Provider, SourceAccount } from '@repo/api-gw-sdk';

import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { NativeVaultColor } from '@/theme/colorSchemes';
import { dayjs, DATE_FORMAT, TIME_FORMAT } from '@/utils/dayjs';

import { SnapshotIndicator } from './SnapshotIndicator';

import { AccountAsVaultTag, VaultTag } from '../vaults/vaultTag';

const recoveryPointText = 'Recovery point';

export const SnapshotToolbar = ({
  pointInTime,
  vaultId,
  accessDeniedItemsCount,
  hideAccessDeniedItemsCount,
  supportPiTR = false,
  awsBackupVaultInfo,
}: {
  pointInTime?: Date | null;
  vaultId?: string | null;
  accessDeniedItemsCount?: number;
  hideAccessDeniedItemsCount?: boolean;
  supportPiTR?: boolean;
  awsBackupVaultInfo?: {
    cloudProvider: Provider;
    region: string;
  };
}) => {
  const { vaults } = useBackupVaults();

  const vault = vaults.find((v) => v.id === vaultId);

  // If no Eon vault and no AWS Backup vault info, don't render
  if (!vault && !awsBackupVaultInfo) {
    return null;
  }

  return (
    <Stack
      sx={{
        background: 'var(--mui-palette-background-default)',
        margin: '0px 40px',
        padding: '16px 24px',
        borderRadius: '4px',
      }}
      divider={<Divider className='my-[12px]' />}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography>
          {supportPiTR ? recoveryPointText : 'Snapshot time'}
        </Typography>
        <Stack direction='row' alignItems='center' gap='12px'>
          <SnapshotIndicator
            backgroundColor={vault?.backgroundColor || NativeVaultColor}
            isPartial={!!accessDeniedItemsCount}
          />
          <Typography>
            {`${dayjs.utc(pointInTime).format(DATE_FORMAT)} at ${dayjs.utc(pointInTime).format(TIME_FORMAT)} UTC`}
          </Typography>
          <Divider orientation='vertical' flexItem className='mx-[12px]' />
          <Typography>Stored in</Typography>
          {vault ? (
            <VaultTag vault={vault} />
          ) : (
            awsBackupVaultInfo && (
              <AccountAsVaultTag
                cloudProvider={awsBackupVaultInfo.cloudProvider}
                region={awsBackupVaultInfo.region}
              />
            )
          )}
        </Stack>
      </Stack>
      {!!accessDeniedItemsCount && !hideAccessDeniedItemsCount && (
        <Typography>
          {accessDeniedItemsCount === 1
            ? 'This is a partial snapshot. 1 file wasn`t backed up.'
            : `This is a partial snapshot. ${
                accessDeniedItemsCount
              } files weren’t backed up.`}
        </Typography>
      )}
    </Stack>
  );
};

export const ContinuousSnapshotToolbar = ({
  pointInTime,
  account,
  region,
}: {
  pointInTime?: Date;
  account?: SourceAccount;
  region: string;
}) => {
  return (
    <Stack
      sx={{
        background: 'var(--mui-palette-background-default)',
        margin: '0px 40px',
        padding: '16px 24px',
        borderRadius: '4px',
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography>{recoveryPointText}</Typography>
        <Stack direction='row' alignItems='center' gap='12px'>
          <SnapshotIndicator backgroundColor={null} isNative={true} />
          <Typography>
            {pointInTime
              ? `${dayjs.utc(pointInTime).format(DATE_FORMAT)} at ${dayjs.utc(pointInTime).format(TIME_FORMAT)} UTC`
              : 'Latest available'}
          </Typography>
          <Divider orientation='vertical' flexItem className='mx-[12px]' />
          <Typography>Stored in</Typography>
          {account ? (
            <AccountAsVaultTag
              cloudProvider={account.sourceAccountAttributes.cloudProvider}
              region={region}
            />
          ) : (
            <Skeleton variant='text' width={100} height={24} />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
